import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useCadenceSocket } from "@/lib/useCadenceSocket";
import { Board, toCard } from "@/components/workspace/Board";
import { PreviewPane } from "@/components/workspace/PreviewPane";
import { ModelSelect, SendModeControl, GoalModal } from "@/components/workspace/Composer";
import { buildBlocks, Block } from "@/components/workspace/TaskNarrative";
import { DiffPanel, LogsPanel, CheckpointsPanel, FilesPanel, GitPanel, IssuesPanel, EmptyTask, HistoryModal } from "@/components/workspace/panels";
import { ProjectSettings } from "@/components/workspace/ProjectSettings";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Send, GitBranch, Circle, Loader2, FileCode, Check, X as XIcon,
  Settings as SettingsIcon,
  Terminal, History, GitCommit, Files, FileDiff, Sparkles, ListChecks, Layers, GitMerge,
  MessageSquare, Pencil, Play, Search as SearchIcon, ChevronDown, Repeat, Square, ListPlus,
  Clock, Archive, GitPullRequest, ExternalLink, Lock, Bot, Monitor, RefreshCw, AlertTriangle,
  Rocket, Menu,
} from "lucide-react";

const MODES = [
  { id: "ask", label: "Ask", icon: MessageSquare },
  { id: "plan", label: "Plan", icon: ListChecks },
  { id: "edit", label: "Edit", icon: Pencil },
  { id: "run", label: "Run", icon: Play },
  // Beast: elevate this session's agent into the full autonomous engine — the prompt
  // becomes the goal, decomposed into parallel, verified, reviewed branches.
  { id: "beast", label: "Beast", icon: Sparkles },
];

// Tailwind `md` — at and above this the rail/drawer render inline; below, as Sheets (audit UX-08).
const MD_QUERY = "(min-width: 768px)";

// LEFT RAIL content — defined once, rendered inline in an <aside> at md+ and inside a
// left-side <Sheet> below md (no copy-paste between the two containers).
function VersionsRail({ project, versions, activeVersionId, runningTaskId, connected, onlineHost,
                        onBack, onAddVersion, onSelectVersion, onShowHistory }) {
  return (
    <>
      <div className="px-3 py-3 border-b border-gray-200">
        <button data-testid="back-to-projects" onClick={onBack} className="text-xs text-gray-400 hover:text-gray-700 inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Projects
        </button>
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg grid place-items-center text-xs font-700" style={{ background: `${project?.color || "#2563EB"}1a`, color: project?.color || "#2563EB" }}>
            {(project?.name || "P").slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="font-700 text-sm truncate text-gray-900">{project?.name || "…"}</div>
            <div className="text-[11px] text-gray-400">{project?.project_type} project</div>
          </div>
        </div>
      </div>

      <div className="px-3 py-2 flex items-center justify-between">
        <span className="label-caps inline-flex items-center gap-1"><Layers className="w-3 h-3" /> Versions</span>
        <button data-testid="new-version-button" onClick={onAddVersion} className="text-gray-400 hover:text-gray-900 p-1 rounded hover:bg-gray-200" title="New version">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto cadence-scroll px-2 space-y-1">
        {versions.map((v) => {
          const active = v.id === activeVersionId;
          const working = active && runningTaskId;
          return (
            <button key={v.id} data-testid="version-item" onClick={() => onSelectVersion(v.id)}
              className={`w-full text-left px-2.5 py-2 rounded-lg transition-colors ${active ? "bg-white border border-gray-200 shadow-sm" : "hover:bg-gray-200/60"}`}>
              <div className="flex items-center gap-2">
                <GitBranch className={`w-3.5 h-3.5 ${active ? "text-gray-900" : "text-gray-400"}`} />
                <span className="text-[13px] text-gray-800 truncate flex-1 font-500">{v.name}</span>
                {working ? <span className="w-1.5 h-1.5 rounded-full bg-blue-500 pulse-dot" />
                  : v.is_main ? <span className="text-[9px] text-gray-400 uppercase tracking-wide">main</span> : null}
              </div>
              <div className="text-[10px] text-gray-400 mt-0.5 ml-5 font-mono truncate">{v.branch} · {v.session_count} session{v.session_count === 1 ? "" : "s"}</div>
            </button>
          );
        })}
      </div>

      <button data-testid="open-history" onClick={onShowHistory}
        className="mx-2 mb-2 px-2.5 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-200/60 inline-flex items-center gap-2">
        <History className="w-4 h-4" /> Session history
      </button>
      <div className="px-3 py-2.5 border-t border-gray-200 text-[11px] flex items-center gap-2">
        {/* host.status pushes can't arrive over a dead socket — treat the host as offline while disconnected */}
        <Circle className={`w-2 h-2 ${connected && onlineHost ? "fill-emerald-500 text-emerald-500" : "fill-gray-300 text-gray-300"}`} />
        <span className="text-gray-500 truncate">{!connected ? "Relay disconnected" : onlineHost ? onlineHost.name : "No host online"}</span>
      </div>
    </>
  );
}

// RIGHT DRAWER content — defined once, rendered inline in an <aside> at md+ and inside a
// right-side <Sheet> below md.
function DrawerPanels({ tab, onTab, diffs, logs, checkpoints, onRestore, issues, tree, openFile, onOpenFile,
                        gitStatus, host, githubLinked, onMerge, onRefreshGit, onSync }) {
  return (
    <>
      <div className="flex items-center border-b border-gray-200 px-2">
        {[
          { id: "diff", icon: FileDiff, label: "Diff" },
          { id: "logs", icon: Terminal, label: "Logs" },
          { id: "checkpoints", icon: History, label: "Saves" },
          { id: "files", icon: Files, label: "Files" },
          { id: "git", icon: GitCommit, label: "Git" },
          { id: "issues", icon: AlertTriangle, label: "Issues" },
        ].map((tb) => (
          <button key={tb.id} data-testid={`drawer-tab-${tb.id}`} onClick={() => onTab(tb.id)}
            className={`flex-1 py-2.5 text-[11px] font-600 inline-flex flex-col items-center gap-1 border-b-2 transition-colors ${tab === tb.id ? "border-gray-900 text-gray-900" : "border-transparent text-gray-400 hover:text-gray-700"}`}>
            <tb.icon className="w-4 h-4" /> {tb.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto cadence-scroll p-3 text-sm">
        {tab === "diff" && <DiffPanel diffs={diffs} />}
        {tab === "logs" && <LogsPanel logs={logs} />}
        {tab === "checkpoints" && <CheckpointsPanel checkpoints={checkpoints} onRestore={onRestore} />}
        {tab === "issues" && <IssuesPanel issues={issues} />}
        {tab === "files" && <FilesPanel tree={tree} openFile={openFile} onOpen={onOpenFile} />}
        {tab === "git" && <GitPanel status={gitStatus} host={host} githubLinked={githubLinked}
          onMerge={onMerge} onRefresh={onRefreshGit} onSync={onSync} />}
      </div>
    </>
  );
}

export default function Workspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [sessionEvents, setSessionEvents] = useState([]);
  const [handoff, setHandoff] = useState(null);  // session handoff incl. pending_issues + mined friction
  const sessionTaskIds = useRef(new Set());
  const optimisticPromptTaskIds = useRef(new Set()); // prompts already rendered locally — drop the relay's echo
  const [hosts, setHosts] = useState([]);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState({ provider: "anthropic", id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" });
  const [mode, setMode] = useState("ask");
  const [prompt, setPrompt] = useState("");
  // Mobile layout (audit UX-08): the drawer starts closed on phones — matchMedia
  // initializer, not a resize effect, so the first render is already correct.
  const [drawer, setDrawer] = useState(() => ({ open: window.matchMedia(MD_QUERY).matches, tab: "diff" }));
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(MD_QUERY).matches);
  const [railOpen, setRailOpen] = useState(false); // versions rail Sheet, below md only
  const [tree, setTree] = useState([]);
  const [openFile, setOpenFile] = useState(null);
  const [resolved, setResolved] = useState({});
  const [runningTaskId, setRunningTaskId] = useState(null);
  const [queue, setQueue] = useState([]);
  const [defaultSend, setDefaultSend] = useState("interrupt");
  const [showHistory, setShowHistory] = useState(false);
  const [sessionMenu, setSessionMenu] = useState(false);
  const [gitStatus, setGitStatus] = useState(null);
  const [boardMode, setBoardMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [orchestrationCards, setOrchestrationCards] = useState([]);
  const [parseNotes, setParseNotes] = useState([]); // planner-JSON auto-repairs surfaced to the operator
  const [proposal, setProposal] = useState(null); // {buildId, goal, rationale} for a Maintainer proposal
  const [showGoal, setShowGoal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(null); // Board card awaiting discard confirmation
  const [confirmRestore, setConfirmRestore] = useState(null); // {commit, branch, is_main} checkpoint awaiting restore confirmation
  const [suggestedGoal, setSuggestedGoal] = useState("");
  const buildAutoApplyRef = useRef(true);
  const buildIdRef = useRef(null);
  const taskChipMap = useRef({});
  const branchChipMap = useRef({});
  const cardTaskMap = useRef({});
  const dispatchingRef = useRef(false);
  const feedRef = useRef(null);
  const composerRef = useRef(null);
  const eventBufRef = useRef([]);
  const flushTimerRef = useRef(null);
  const pollRef = useRef(null);

  function flushEvents() {
    flushTimerRef.current = null;
    if (eventBufRef.current.length === 0) return;
    const batch = eventBufRef.current;
    eventBufRef.current = [];
    setSessionEvents((prev) => [...prev, ...batch]);
  }
  // Batch streamed tokens: coalesce consecutive narrative deltas and commit at ~60ms
  // so the feed re-renders a few times/second instead of once per token.
  function queueEvent(ev) {
    const buf = eventBufRef.current;
    const last = buf[buf.length - 1];
    if (ev.kind === "narrative_delta" && last && last.kind === "narrative_delta") {
      last.text = (last.text || "") + (ev.text || "");
    } else {
      buf.push(ev);
    }
    if (!flushTimerRef.current) flushTimerRef.current = setTimeout(flushEvents, 60);
  }

  const onlineHost = hosts.find((h) => h.online);
  const activeVersion = useMemo(() => versions.find((v) => v.id === activeVersionId), [versions, activeVersionId]);

  const { send, connected } = useCadenceSocket((msg) => {
    if (msg.type === "task.event") {
      if (sessionTaskIds.current.has(msg.task_id)) {
        // skip the relay's echo of a prompt we already appended optimistically in dispatch()
        if (!(msg.event.kind === "prompt" && optimisticPromptTaskIds.current.delete(msg.task_id))) {
          queueEvent(msg.event);
        }
      }
      const ev = msg.event;
      if (ev.kind === "status") {
        if (["done", "error", "cancelled"].includes(ev.status) && msg.task_id === runningTaskId) {
          setRunningTaskId(null);
          dispatchingRef.current = false;
        }
        if (cardTaskMap.current[msg.task_id] && ["done", "error"].includes(ev.status)) {
          const st = ev.status === "done" ? "ready" : "failed";
          setOrchestrationCards((cs) => cs.map((c) => c.taskId === msg.task_id ? { ...c, status: st } : c));
          const chipId = taskChipMap.current[msg.task_id];
          if (chipId) api.patch(`/chips/${chipId}`, { status: st }).catch(() => {});
        }
      }
      if (ev.kind === "applied" || ev.kind === "diff_proposal") setDrawer((d) => ({ ...d, open: true, tab: "diff" }));
      if (ev.kind === "git") setGitStatus(ev.status);
    } else if (msg.type === "host.status") {
      setHosts((hs) => hs.map((h) => h.host_id === msg.host_id ? { ...h, online: msg.online } : h));
    } else if (msg.type === "fs.tree.result") {
      setTree(msg.tree || []);
    } else if (msg.type === "fs.read.result") {
      setOpenFile({ path: msg.path, content: msg.content });
    } else if (msg.type === "git.status.result") {
      setGitStatus(msg.status);
    } else if (msg.type === "git.merge.result") {
      const r = msg.result || {};
      toast[r.ok ? "success" : "error"](r.ok ? `Merged ${r.branch} into main` : `Merge failed: ${r.output?.slice(0, 80)}`);
      if (r.ok) {
        setOrchestrationCards((cs) => cs.map((c) => c.branch === r.branch ? { ...c, status: "merged" } : c));
        const chipId = branchChipMap.current[r.branch];
        if (chipId) api.patch(`/chips/${chipId}`, { status: "merged" }).catch(() => {});
      }
      if (activeVersion) send({ type: "git.status", host_id: onlineHost?.host_id, branch: activeVersion.branch, is_main: activeVersion.is_main, project_path: project?.root_path });
    } else if (msg.type === "checkpoint.restore.result") {
      const r = msg.result || {};
      toast[r.ok ? "success" : "error"](r.ok ? `Restored ${r.branch} to ${r.restored_to}` : `Restore failed: ${r.error || "unknown error"}`);
      if (r.ok && activeVersion) send({ type: "git.status", host_id: onlineHost?.host_id, branch: activeVersion.branch, is_main: activeVersion.is_main, project_path: project?.root_path });
    }
  });

  // keep isDesktop live across viewport changes (orientation flips, window resizes)
  useEffect(() => {
    const mq = window.matchMedia(MD_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // bootstrap
  const [bootError, setBootError] = useState(null); // null | "notfound" | "error"
  const [bootTick, setBootTick] = useState(0); // bump to retry the bootstrap
  useEffect(() => {
    (async () => {
      try {
      setBootError(null);
      const [p, h, pr, v] = await Promise.all([
        api.get(`/projects/${projectId}`), api.get("/hosts"), api.get("/providers"),
        api.get(`/projects/${projectId}/versions`),
      ]);
      setProject(p.data.project);
      setHosts(h.data.hosts);
      const flat = [];
      pr.data.providers.forEach((prov) => prov.connected && prov.models.forEach((m) => flat.push({ provider: prov.provider, id: m.id, label: m.label })));
      if (flat.length === 0) pr.data.providers.forEach((prov) => prov.models.forEach((m) => flat.push({ provider: prov.provider, id: m.id, label: m.label })));
      setModels(flat);
      if (flat[0]) setModel(flat[0]);
      setVersions(v.data.versions);
      const main = v.data.versions.find((x) => x.is_main) || v.data.versions[0];
      setActiveVersionId(main?.id);
      // hydrate the Board from the durable substrate so it survives refresh + host restart
      try {
        const lb = await api.get(`/projects/${projectId}/builds`);
        const chips = lb.data.chips || [];
        if (lb.data.build && chips.length) {
          buildIdRef.current = lb.data.build.id;
          buildAutoApplyRef.current = lb.data.build.auto_apply;
          setParseNotes(lb.data.build.parse_notes || []);
          const cards = chips.map(toCard);
          cards.forEach((c) => {
            if (c.taskId) { cardTaskMap.current[c.taskId] = c.versionId; taskChipMap.current[c.taskId] = c.chipId; }
            if (c.branch) branchChipMap.current[c.branch] = c.chipId;
          });
          setOrchestrationCards(cards);
          if (lb.data.build.source === "maintainer" && lb.data.build.status === "proposed") {
            setProposal({ buildId: lb.data.build.id, goal: lb.data.build.goal, rationale: lb.data.build.rationale });
          }
          // resume the scheduler for an in-flight build + keep the Board live
          if (lb.data.build.status === "running") {
            api.post(`/builds/${lb.data.build.id}/schedule`).catch(() => {});
            startChipPolling();
          }
        }
      } catch (e) { /* substrate optional */ }
      } catch (e) {
        setBootError(e?.response?.status === 404 ? "notfound" : "error");
      }
    })();
    // eslint-disable-next-line
  }, [projectId, bootTick]);

  // load sessions when version changes
  useEffect(() => {
    if (!activeVersionId) return;
    (async () => {
      const { data } = await api.get(`/projects/${projectId}/sessions`, { params: { version_id: activeVersionId } });
      let list = data.sessions;
      if (list.length === 0) {
        const created = await api.post(`/projects/${projectId}/sessions`, { version_id: activeVersionId });
        list = [created.data.session];
      }
      setSessions(list);
      openSession(list[0].id);
    })();
    // eslint-disable-next-line
  }, [activeVersionId]);

  useEffect(() => {
    if (connected && onlineHost) {
      send({ type: "fs.tree", host_id: onlineHost.host_id, branch: activeVersion?.branch || "main", is_main: activeVersion?.is_main ?? true, project_path: project?.root_path });
    }
    // eslint-disable-next-line
  }, [connected, onlineHost?.host_id, activeVersionId]);

  // activation moment: a freshly imported repo opens the Parallel build modal pre-seeded
  useEffect(() => {
    if (project && searchParams.get("suggest") === "parallel") {
      setSuggestedGoal(`Improve ${project.name}: tighten the README, add a couple of basic tests, and polish the main entry file. Split the work into independent branches.`);
      setBoardMode(true);
      setShowGoal(true);
      searchParams.delete("suggest");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [project]);

  useEffect(() => () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }, [projectId]);

  // refresh git status when the Git drawer is open / version changes
  useEffect(() => {
    if (connected && onlineHost && drawer.open && drawer.tab === "git") {
      send({ type: "git.status", host_id: onlineHost.host_id, branch: activeVersion?.branch || "main", is_main: activeVersion?.is_main ?? true, project_path: project?.root_path });
    }
    // eslint-disable-next-line
  }, [drawer.open, drawer.tab, activeVersionId, connected, onlineHost?.host_id]);

  // queue runner
  useEffect(() => {
    if (runningTaskId === null && !dispatchingRef.current && queue.length > 0) {
      const next = queue[0];
      setQueue((q) => q.slice(1));
      dispatch(next);
    }
    // eslint-disable-next-line
  }, [runningTaskId, queue]);

  // load the session handoff (pending issues + mined friction) whenever the session changes
  useEffect(() => {
    if (!session?.id) { setHandoff(null); return; }
    let alive = true;
    api.get(`/sessions/${session.id}/handoff`)
      .then((r) => { if (alive) setHandoff(r.data.handoff || null); })
      .catch(() => { if (alive) setHandoff(null); });
    return () => { alive = false; };
  }, [session?.id]);

  async function openSession(sessionId) {
    const { data } = await api.get(`/sessions/${sessionId}`);
    setSession(data.session);
    setSessionEvents(data.events);
    sessionTaskIds.current = new Set(data.tasks.map((t) => t.id));
    setSessionMenu(false);
  }

  async function newSession(versionId = activeVersionId) {
    const { data } = await api.post(`/projects/${projectId}/sessions`, { version_id: versionId });
    setSessions((s) => [data.session, ...s]);
    setSession(data.session);
    setSessionEvents([]);
    sessionTaskIds.current = new Set();
    setSessionMenu(false);
  }

  async function archiveSession() {
    if (!session) return;
    await api.patch(`/sessions/${session.id}`, { status: "archived" });
    setSessions((s) => s.filter((x) => x.id !== session.id));
    const rest = sessions.filter((x) => x.id !== session.id);
    if (rest[0]) openSession(rest[0].id); else newSession();
  }

  async function addVersion() {
    const name = window.prompt("New version name (e.g. feature/login):");
    if (!name) return;
    const { data } = await api.post(`/projects/${projectId}/versions`, { name });
    setVersions((v) => [...v, data.version]);
    setActiveVersionId(data.version.id);
  }

  async function dispatch(input) {
    const { text, mode: m, modelId, provider } = input;
    if (!onlineHost) { dispatchingRef.current = false; toast.error("Pair a host in Integrations first"); return; }
    // there is no PATCH /tasks endpoint to un-strand a task, so refuse to create one over a dead socket
    if (!connected) { dispatchingRef.current = false; toast.error("Connection lost — message not sent"); return; }
    if (session && session.title === "New session") {
      const title = text.slice(0, 60);
      api.patch(`/sessions/${session.id}`, { title });
      setSession((s) => ({ ...s, title }));
      setSessions((ss) => ss.map((x) => x.id === session.id ? { ...x, title } : x));
    }
    const { data } = await api.post(`/projects/${projectId}/tasks`, {
      title: text.slice(0, 70), mode: m, model: modelId, provider, session_id: session?.id,
    });
    const task = data.task;
    sessionTaskIds.current.add(task.id);
    // render the user's message immediately; the relay echoes it back and the handler drops the echo
    optimisticPromptTaskIds.current.add(task.id);
    setSessionEvents((prev) => [...prev, { kind: "prompt", text, mode: m, model: modelId }]);
    setRunningTaskId(task.id);
    dispatchingRef.current = true;
    const sent = send({
      type: "task.run", task_id: task.id, host_id: onlineHost?.host_id, project_id: projectId,
      prompt: text, mode: m, model: modelId, provider, cwd: project?.root_path, project_path: project?.root_path,
      branch: activeVersion?.branch || "main", is_main: activeVersion?.is_main ?? true,
    });
    if (!sent) {
      // socket died between the guard and the send — don't leave the UI stuck on a task that never ran
      optimisticPromptTaskIds.current.delete(task.id);
      setRunningTaskId(null);
      dispatchingRef.current = false;
      toast.error("Connection lost — message not sent");
    }
  }

  function submit(flip = false) {
    const text = prompt.trim();
    if (!text) return;
    // Beast mode: the prompt IS the goal — launch a beastmode build instead of a
    // single task. It runs independently of any in-flight interactive task.
    if (mode === "beast") {
      setPrompt("");
      orchestrate(text, { autoApply: true, autoMerge: false });
      return;
    }
    const payload = { text, mode, modelId: model.id, provider: model.provider };
    setPrompt("");
    if (runningTaskId) {
      const behavior = flip ? (defaultSend === "interrupt" ? "queue" : "interrupt") : defaultSend;
      if (behavior === "queue") { setQueue((q) => [...q, payload]); return; }
      send({ type: "task.cancel", host_id: onlineHost?.host_id, task_id: runningTaskId });
      dispatchingRef.current = true;
      dispatch(payload);
    } else {
      dispatch(payload);
    }
  }

  function cancelRunning() {
    if (runningTaskId) send({ type: "task.cancel", host_id: onlineHost?.host_id, task_id: runningTaskId });
  }

  function approve(proposalId, ok) {
    setResolved((r) => ({ ...r, [proposalId]: ok ? "approved" : "rejected" }));
    send({ type: ok ? "task.approve" : "task.reject", host_id: onlineHost?.host_id, task_id: runningTaskId, proposal_id: proposalId });
  }

  // Beastmode: elevate THIS session's agent into the full autonomous Cadence engine.
  // A single server-side call decomposes the goal (strong planner), creates one
  // isolated version/session/task/chip per sub-task (chips inherit this session's
  // agent), anchors the build to the current session, and hands the DAG to the
  // scheduler. Replaces the old build-create + WS-orchestrate + per-chip choreography.
  async function orchestrate(goal, opts) {
    if (!goal.trim() || !onlineHost) { if (!onlineHost) toast.error("Pair a host first."); return; }
    setShowGoal(false);
    setBoardMode(true);
    const tId = "beastmode";
    toast.message("Orchestrating…", { id: tId, description: "Decomposing your goal into a dependency-aware plan." });
    try {
      const { data } = await api.post(`/projects/${projectId}/beastmode`, {
        goal,
        session_id: session?.id || "",
        version_id: activeVersionId || "",
        provider: model.provider,
        model: model.id,
        auto_apply: opts?.autoApply ?? true,
        auto_merge: opts?.autoMerge ?? false,
      });
      buildIdRef.current = data.build.id;
      buildAutoApplyRef.current = data.build.auto_apply;
      setParseNotes(data.build.parse_notes || []);
      const cards = (data.chips || []).map(toCard);
      cards.forEach((c) => {
        if (c.taskId) { cardTaskMap.current[c.taskId] = c.versionId; taskChipMap.current[c.taskId] = c.chipId; }
        if (c.branch) branchChipMap.current[c.branch] = c.chipId;
      });
      setOrchestrationCards(cards);
      const vlist = await api.get(`/projects/${projectId}/versions`);
      setVersions(vlist.data.versions);
      startChipPolling();
      toast.success(`Planned ${cards.length} chips. Scheduler is running them by dependency.`, { id: tId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Beastmode failed to start.", { id: tId });
    }
  }

  function startChipPolling() {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/builds`);
        const chips = data.chips || [];
        setParseNotes(data.build?.parse_notes || []);
        if (chips.length) {
          setOrchestrationCards(chips.map(toCard));
          chips.forEach((c) => {
            if (c.task_id) { cardTaskMap.current[c.task_id] = c.version_id; taskChipMap.current[c.task_id] = c.id; }
            if (c.branch) branchChipMap.current[c.branch] = c.id;
          });
        }
        if (data.build && ["done", "failed"].includes(data.build.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
        }
      } catch (e) { /* keep polling */ }
    }, 2500);
  }

  async function mergeCard(card) {
    if (!card.chipId) {
      send({ type: "git.merge", host_id: onlineHost?.host_id, branch: card.branch, project_path: project?.root_path });
      return;
    }
    const tid = toast.loading(`Merging ${card.branch}…`);
    try {
      const { data } = await api.post(`/chips/${card.chipId}/merge`);
      if (data.chip.status === "merged") toast.success(`Merged ${card.branch} into main`, { id: tid });
      else toast.error(`Merge blocked: ${data.chip.verification?.merge_error || "integration check failed"}`, { id: tid });
      startChipPolling();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { id: tid });
    }
  }
  async function addChipToBoard(title) {
    if (!buildIdRef.current) { toast.error("Start a build before adding a chip."); return; }
    const slug = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32) || "chip";
    const branch = `chip/${slug}-${Date.now().toString(36).slice(-6)}`;
    const tid = toast.loading("Adding chip…");
    try {
      const { data } = await api.post(`/builds/${buildIdRef.current}/chips`, { title: title.trim(), branch });
      setOrchestrationCards((cs) => [...cs, toCard(data.chip)]);
      toast.success("Chip added", { id: tid });
      startChipPolling();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { id: tid });
    }
  }
  async function clearBuild() {
    if (!buildIdRef.current) { setOrchestrationCards([]); return; }
    if (!window.confirm("Clear this build and its agent cards?")) return;
    try { await api.delete(`/builds/${buildIdRef.current}`); } catch (e) { /* best-effort */ }
    buildIdRef.current = null;
    setProposal(null);
    setOrchestrationCards([]);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    toast.success("Board cleared");
  }
  async function runMaintainer() {
    const tid = toast.loading("Maintainer scanning the repo…");
    try {
      const { data } = await api.post(`/projects/${projectId}/maintainer/run`);
      if (data.error) { toast(data.detail || "Maintainer found nothing actionable.", { id: tid, icon: "🛈" }); return; }
      const b = data.build;
      buildIdRef.current = b.id;
      buildAutoApplyRef.current = b.auto_apply;
      setProposal({ buildId: b.id, goal: b.goal, rationale: b.rationale });
      hydrateProposedChips(data.chips);
      toast.success("Maintainer proposed a plan — review & approve", { id: tid });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { id: tid });
    }
  }
  function hydrateProposedChips(chips) {
    // Proposed maintainer chips have null task/session/PR fields, so the full
    // toCard shape is behavior-identical to the previous trimmed mapping here.
    setOrchestrationCards((chips || []).map(toCard));
  }
  async function approveProposal() {
    if (!proposal) return;
    const tid = toast.loading("Approving plan…");
    try {
      await api.post(`/builds/${proposal.buildId}/approve_plan`);
      setProposal(null);
      toast.success("Plan approved — agents are starting", { id: tid });
      startChipPolling();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { id: tid });
    }
  }
  async function dismissProposal() {
    if (!proposal) return;
    try { await api.delete(`/builds/${proposal.buildId}`); } catch (e) { /* best-effort */ }
    buildIdRef.current = null;
    setProposal(null);
    setOrchestrationCards([]);
    toast("Proposal dismissed");
  }
  async function pushCard(card) {
    if (!project?.github_full_name) { toast.error("Import a GitHub repo to enable push."); return; }
    const tid = toast.loading(`Pushing ${card.branch} to GitHub…`);
    try {
      await api.post("/github/push", { project_id: projectId, branch: card.branch });
      toast.success(`Pushed ${card.branch} to GitHub`, { id: tid });
      setOrchestrationCards((cs) => cs.map((c) => c.versionId === card.versionId ? { ...c, pushed: true } : c));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { id: tid });
    }
  }
  async function syncFromGitHub() {
    if (!project?.github_full_name) { toast.error("Import a GitHub repo to enable sync."); return; }
    const branch = activeVersion?.branch || "main";
    const tid = toast.loading(`Syncing ${branch} from GitHub…`);
    try {
      const { data } = await api.post("/github/pull", { project_id: projectId, branch });
      if (data.ok) {
        toast.success(`Pulled to ${data.updated_to}`, { id: tid });
        send({ type: "git.status", host_id: onlineHost?.host_id, branch, is_main: activeVersion?.is_main ?? true, project_path: project?.root_path });
      } else if (data.error === "diverged") {
        toast.error(`Local and GitHub have diverged (${data.ahead} ahead / ${data.behind} behind) — resolve manually`, { id: tid });
      } else {
        toast.error(`Sync failed: ${data.error || data.output || "unknown error"}`, { id: tid });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { id: tid });
    }
  }
  async function openPR(card) {
    const tid = toast.loading(`Opening PR for ${card.branch}…`);
    try {
      const { data } = await api.post("/github/pr", { project_id: projectId, branch: card.branch, title: card.title });
      toast.success("Pull request opened", { id: tid, action: { label: "View", onClick: () => window.open(data.html_url, "_blank") } });
      setOrchestrationCards((cs) => cs.map((c) => c.versionId === card.versionId ? { ...c, prUrl: data.html_url } : c));
      const chipId = card.chipId || branchChipMap.current[card.branch];
      if (chipId) api.patch(`/chips/${chipId}`, { pr_url: data.html_url, pr_number: data.number }).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { id: tid });
    }
  }
  function discardCard(card) {
    setConfirmDiscard(card);
  }
  async function performDiscard(card) {
    // a working/verifying card has a live task on the host — cancel it before dropping the branch
    if (["working", "verifying"].includes(card.status) && card.taskId) {
      send({ type: "task.cancel", host_id: onlineHost?.host_id, task_id: card.taskId });
    }
    await api.delete(`/versions/${card.versionId}`).catch(() => {});
    setOrchestrationCards((cs) => cs.filter((c) => c.versionId !== card.versionId));
    const vlist = await api.get(`/projects/${projectId}/versions`);
    setVersions(vlist.data.versions);
    toast.success(`Discarded ${card.branch}`);
  }
  function stopCard(card) {
    if (card.taskId) send({ type: "task.cancel", host_id: onlineHost?.host_id, task_id: card.taskId });
    if (card.chipId) api.patch(`/chips/${card.chipId}`, { status: "failed" }).catch(() => {});
    setOrchestrationCards((cs) => cs.map((c) => c.versionId === card.versionId ? { ...c, status: "failed" } : c));
    toast.success(`Stopped ${card.branch}`);
  }
  function openVersionFromBoard(versionId) { setBoardMode(false); setActiveVersionId(versionId); }
  async function approveChip(card) {
    if (!card.chipId) { toast.error("This chip isn't ready to approve yet."); return; }
    const tid = toast.loading("Approving…");
    try {
      await api.post(`/chips/${card.chipId}/approve`);
      toast.success("Approved — the scheduler will proceed", { id: tid });
      setOrchestrationCards((cs) => cs.map((c) => c.chipId === card.chipId ? { ...c, status: "queued" } : c));
      startChipPolling();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { id: tid });
    }
  }

  // One-click deploy: POST /projects/{pid}/deploy-now ships the configured target
  // (local sim or Cloudflare Pages) on the project's default branch. The host response
  // is {ok, command, output, url}; history lands in the deploys collection server-side
  // and Project settings re-fetches it on open.
  async function deployNow() {
    if (!onlineHost || deploying) return;
    setDeploying(true);
    const tid = toast.loading("Deploying…", { description: "Building and shipping the configured deploy target." });
    try {
      // 504 (deploy timeout) is a 5xx — skipErrorToast so the interceptor doesn't double-toast
      const { data } = await api.post(`/projects/${projectId}/deploy-now`, {}, { skipErrorToast: true });
      if (data.ok) {
        toast.success("Deployed", {
          id: tid, description: data.url || undefined,
          action: data.url ? { label: "Open", onClick: () => window.open(data.url, "_blank") } : undefined,
        });
      } else {
        toast.error(`Deploy failed: ${(data.output || "no output").slice(0, 140)}`, { id: tid });
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail), { id: tid });
    } finally {
      setDeploying(false);
    }
  }

  const blocks = useMemo(() => buildBlocks(sessionEvents), [sessionEvents]);
  const logs = sessionEvents.filter((e) => e.kind === "log");
  const checkpoints = sessionEvents.filter((e) => e.kind === "checkpoint");
  const diffs = sessionEvents.filter((e) => e.kind === "diff_proposal" || e.kind === "applied");

  useEffect(() => { feedRef.current && (feedRef.current.scrollTop = feedRef.current.scrollHeight); }, [blocks.length, sessionEvents.length]);

  // Single instances of the rail/drawer content — the isDesktop flag picks the
  // container (inline <aside> at md+, Sheet below md), never both at once.
  const railContent = (
    <VersionsRail project={project} versions={versions} activeVersionId={activeVersionId}
      runningTaskId={runningTaskId} connected={connected} onlineHost={onlineHost}
      onBack={() => navigate("/projects")} onAddVersion={addVersion}
      onSelectVersion={(id) => { setActiveVersionId(id); setRailOpen(false); }}
      onShowHistory={() => { setShowHistory(true); setRailOpen(false); }} />
  );
  const drawerPanels = (
    <DrawerPanels tab={drawer.tab} onTab={(t) => setDrawer({ open: true, tab: t })}
      diffs={diffs} logs={logs} checkpoints={checkpoints} issues={handoff?.pending_issues || []}
      tree={tree} openFile={openFile} gitStatus={gitStatus} host={onlineHost} githubLinked={!!project?.github_full_name}
      onRestore={onlineHost ? (c, commit) => setConfirmRestore({
        commit,
        branch: c.branch || activeVersion?.branch || "main",
        is_main: c.branch ? c.branch === "main" : (activeVersion?.is_main ?? true),
      }) : undefined}
      onOpenFile={(path) => { if (onlineHost) { send({ type: "fs.read", host_id: onlineHost.host_id, path, branch: activeVersion?.branch || "main", is_main: activeVersion?.is_main ?? true, project_path: project?.root_path }); } }}
      onMerge={() => { send({ type: "git.merge", host_id: onlineHost?.host_id, branch: activeVersion?.branch, project_path: project?.root_path }); }}
      onRefreshGit={() => { send({ type: "git.status", host_id: onlineHost?.host_id, branch: activeVersion?.branch || "main", is_main: activeVersion?.is_main ?? true, project_path: project?.root_path }); }}
      onSync={syncFromGitHub} />
  );

  if (bootError) {
    return (
      <div className="flex-1 grid place-items-center p-8">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center max-w-sm w-full shadow-sm" data-testid="workspace-boot-error">
          {bootError === "notfound" ? (
            <>
              <h2 className="font-heading font-700 text-lg text-gray-900">Project not found</h2>
              <p className="text-sm text-gray-500 mt-2">It may have been deleted, or this link is out of date.</p>
              <button data-testid="workspace-back-to-projects" onClick={() => navigate("/projects")}
                className="mt-5 bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900">
                Back to projects
              </button>
            </>
          ) : (
            <>
              <h2 className="font-heading font-700 text-lg text-gray-900">Couldn't load this workspace</h2>
              <p className="text-sm text-gray-500 mt-2">Something went wrong while loading the project. Check your connection and try again.</p>
              <button data-testid="workspace-boot-retry" onClick={() => setBootTick((t) => t + 1)}
                className="mt-5 bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900">
                Retry
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* LEFT RAIL — VERSIONS / WORKTREES (inline at md+, hamburger Sheet below md) */}
      {isDesktop ? (
        <aside className="w-64 flex flex-col border-r border-gray-200 bg-gray-50/60 flex-shrink-0">
          {railContent}
        </aside>
      ) : (
        <Sheet open={railOpen} onOpenChange={setRailOpen}>
          <SheetContent side="left" aria-describedby={undefined} data-testid="mobile-rail-sheet"
            className="w-72 p-0 gap-0 flex flex-col bg-gray-50">
            <SheetTitle className="sr-only">Versions</SheetTitle>
            {railContent}
          </SheetContent>
        </Sheet>
      )}

      {/* CENTER CANVAS */}
      <main className="flex-1 flex flex-col relative bg-white overflow-hidden min-w-0">
        <header className="h-14 flex items-center justify-between px-4 md:px-6 border-b border-gray-200 flex-shrink-0 gap-3">
          <button data-testid="mobile-rail-toggle" onClick={() => setRailOpen(true)} title="Versions & sessions"
            className="md:hidden -ml-1 flex-shrink-0 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md p-1.5">
            <Menu className="w-5 h-5" />
          </button>
          <div className="relative min-w-0 flex-1">
            <button data-testid="session-menu-button" onClick={() => setSessionMenu((o) => !o)}
              className="inline-flex items-center gap-2 hover:bg-gray-100 rounded-lg px-2 py-1 -ml-2 max-w-full">
              <span className="font-heading font-700 text-[15px] text-gray-900 truncate">{session?.title || "New session"}</span>
              <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>
            {sessionMenu && (
              <div className="absolute top-full mt-1 left-0 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 w-72 z-40">
                <button data-testid="new-session-button" onClick={() => newSession()}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 inline-flex items-center gap-2 font-500">
                  <Plus className="w-4 h-4" /> New session
                </button>
                <div className="px-3 pt-2 pb-1 label-caps">Recent in {activeVersion?.name}</div>
                <div className="max-h-60 overflow-y-auto cadence-scroll">
                  {sessions.map((s) => (
                    <button key={s.id} data-testid="session-item" onClick={() => openSession(s.id)}
                      className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 flex items-center gap-2 ${s.id === session?.id ? "text-gray-900 font-500" : "text-gray-600"}`}>
                      <Clock className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" /> <span className="truncate">{s.title}</span>
                    </button>
                  ))}
                </div>
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button data-testid="archive-session" onClick={archiveSession} className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 inline-flex items-center gap-2">
                    <Archive className="w-4 h-4" /> Archive this session
                  </button>
                  <button onClick={() => { setSessionMenu(false); setShowHistory(true); }} className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 inline-flex items-center gap-2">
                    <History className="w-4 h-4" /> View all history
                  </button>
                </div>
              </div>
            )}
            <div className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5"><GitBranch className="w-3 h-3" /> {activeVersion?.branch || "main"}</div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button data-testid="header-deploy" onClick={deployNow} disabled={!onlineHost || deploying}
              title={!onlineHost ? "Pair a host in Integrations first" : "Deploy this project"}
              className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5 whitespace-nowrap disabled:opacity-40 disabled:hover:bg-transparent">
              {deploying ? <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" /> : <Rocket className="w-3.5 h-3.5 text-emerald-600" />}
              <span className="hidden lg:inline">{deploying ? "Deploying…" : "Deploy"}</span>
            </button>
            <button data-testid="header-parallel-build" onClick={() => setShowGoal(true)}
              className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5 whitespace-nowrap">
              <Sparkles className="w-3.5 h-3.5 text-blue-600" /> <span className="hidden lg:inline">Parallel build</span>
            </button>
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              {/* below md the view labels collapse to icons so the action row fits a phone */}
              <button data-testid="view-console" onClick={() => { setBoardMode(false); setPreviewMode(false); }} className={`px-2.5 py-1 rounded-md text-xs font-600 inline-flex items-center ${!boardMode && !previewMode ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}><Terminal className="w-3.5 h-3.5 md:hidden" /><span className="hidden md:inline">Console</span></button>
              <button data-testid="view-board" onClick={() => { setBoardMode(true); setPreviewMode(false); }} className={`px-2.5 py-1 rounded-md text-xs font-600 inline-flex items-center ${boardMode && !previewMode ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}><Layers className="w-3.5 h-3.5 md:hidden" /><span className="hidden md:inline">Board</span></button>
              <button data-testid="view-preview" onClick={() => { setPreviewMode(true); setBoardMode(false); }} className={`px-2.5 py-1 rounded-md text-xs font-600 inline-flex items-center ${previewMode ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}><Monitor className="w-3.5 h-3.5 md:hidden" /><span className="hidden md:inline">Preview</span></button>
            </div>
            <button data-testid="drawer-toggle" onClick={() => setDrawer((d) => ({ ...d, open: !d.open }))}
              className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md p-1.5"><FileDiff className="w-4.5 h-4.5" /></button>
            <button data-testid="project-settings-button" onClick={() => setShowSettings(true)} title="Project settings"
              className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md p-1.5"><SettingsIcon className="w-4.5 h-4.5" /></button>
          </div>
        </header>

        {!connected && (
          <div data-testid="connection-banner" className="px-6 py-1.5 bg-amber-50 border-b border-amber-200 text-[12px] text-amber-800 flex items-center gap-2 flex-shrink-0">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reconnecting to Cadence relay…
          </div>
        )}

        {previewMode ? (
          <PreviewPane projectId={projectId} branch={activeVersion?.branch || "main"} host={onlineHost}
            onSuggest={(s) => {
              // the composer only renders outside preview mode — flip back, then focus after the re-render
              setPreviewMode(false);
              setPrompt(s);
              setTimeout(() => composerRef.current?.focus(), 0);
            }} />
        ) : boardMode ? (
          <Board cards={orchestrationCards} parseNotes={parseNotes} versions={versions} githubLinked={!!project?.github_full_name}
            onOpen={openVersionFromBoard} onMerge={mergeCard} onDiscard={discardCard} onStop={stopCard} onNew={() => setShowGoal(true)}
            onPush={pushCard} onPR={openPR} onApprove={approveChip} onClear={clearBuild}
            onRunMaintainer={runMaintainer} proposal={proposal} onApprovePlan={approveProposal} onDismissProposal={dismissProposal}
            onAddChip={addChipToBoard} />
        ) : (
        <div ref={feedRef} className="flex-1 overflow-y-auto cadence-scroll px-6 md:px-12 py-8 pb-44">
          <div className="max-w-3xl mx-auto w-full">
            {blocks.length === 0 && <EmptyTask host={onlineHost} project={project} version={activeVersion}
              onSuggest={(s) => { setPrompt(s); composerRef.current?.focus(); }} />}
            {blocks.map((b, i) => <Block key={i} b={b} resolved={resolved} onApprove={approve} />)}
          </div>
        </div>
        )}

        {/* COMPOSER */}
        {!boardMode && !previewMode && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[min(100%-3rem,780px)] z-20">
          {queue.length > 0 && (
            <div data-testid="queue-strip" className="mb-2 bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-sm px-3 py-2 flex items-center gap-2 flex-wrap">
              <span className="label-caps">Queued · {queue.length}</span>
              {queue.map((qi, i) => (
                <span key={i} className="text-xs bg-gray-100 text-gray-600 rounded-md px-2 py-1 inline-flex items-center gap-1 max-w-[220px]">
                  <span className="truncate">{qi.text}</span>
                  <button onClick={() => setQueue((q) => q.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-gray-700"><XIcon className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="bg-white/95 backdrop-blur-md border border-gray-300 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.10)] p-3">
            <textarea data-testid="composer-prompt-input" ref={composerRef} value={prompt} rows={2}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  if (e.nativeEvent.isComposing) return;
                  e.preventDefault();
                  submit(e.metaKey || e.ctrlKey);
                }
              }}
              placeholder={!onlineHost ? "Pair a host in Integrations to run tasks…" : mode === "beast" ? "Describe a goal — Beast mode plans it into parallel, verified branches… ↵ to launch" : "Describe a task… ↵ to send"}
              className="w-full resize-none border-0 bg-transparent px-2 pt-1 text-sm leading-relaxed placeholder-gray-400 outline-none max-h-40 cadence-scroll" />
            <div className="flex items-center justify-between gap-2 mt-1">
              {/* flex-wrap only matters below md — at desktop widths nothing wraps */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                  {MODES.map((m) => (
                    <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)}
                      className={`px-2.5 py-1 rounded-md text-xs font-600 inline-flex items-center gap-1 transition-colors ${mode === m.id ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-800"}`}>
                      <m.icon className="w-3.5 h-3.5" /> <span className="hidden md:inline">{m.label}</span>
                    </button>
                  ))}
                </div>
                <ModelSelect models={models} model={model} onChange={setModel} />
                <SendModeControl value={defaultSend} onChange={setDefaultSend} />
              </div>
              {runningTaskId ? (
                <button data-testid="composer-stop" onClick={cancelRunning} title="Stop the running task"
                  className="bg-red-600 text-white rounded-lg w-9 h-9 grid place-items-center hover:bg-red-700 transition-colors">
                  <Square className="w-3.5 h-3.5 fill-white" />
                </button>
              ) : (
                <button data-testid="composer-send" onClick={() => submit(false)} disabled={!prompt.trim() || !onlineHost}
                  title={!onlineHost ? "Pair a host in Integrations first" : undefined}
                  className="bg-black text-white rounded-lg w-9 h-9 grid place-items-center hover:bg-gray-900 disabled:opacity-40 transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        )}
      </main>

      {/* RIGHT DRAWER (inline flex sibling at md+, right Sheet below md) */}
      {isDesktop ? (drawer.open && (
        <aside className="w-80 border-l border-gray-200 bg-white flex flex-col flex-shrink-0">
          {drawerPanels}
        </aside>
      )) : (
        <Sheet open={drawer.open} onOpenChange={(open) => setDrawer((d) => ({ ...d, open }))}>
          <SheetContent side="right" aria-describedby={undefined} data-testid="mobile-drawer-sheet"
            className="w-[85vw] p-0 gap-0 pt-8 flex flex-col">
            <SheetTitle className="sr-only">Inspector</SheetTitle>
            {drawerPanels}
          </SheetContent>
        </Sheet>
      )}

      {showHistory && <HistoryModal projectId={projectId} onClose={() => setShowHistory(false)}
        onOpen={(s) => { setShowHistory(false); setActiveVersionId(s.version_id); setTimeout(() => openSession(s.id), 50); }} />}
      {showGoal && <GoalModal initial={suggestedGoal} onClose={() => { setShowGoal(false); setSuggestedGoal(""); }} onSubmit={orchestrate} />}
      {showSettings && project && <ProjectSettings project={project} onClose={() => setShowSettings(false)}
        onSaved={() => api.get(`/projects/${projectId}`).then(({ data }) => setProject(data.project)).catch(() => {})} />}

      <AlertDialog open={!!confirmDiscard} onOpenChange={(open) => { if (!open) setConfirmDiscard(null); }}>
        <AlertDialogContent data-testid="discard-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard branch {confirmDiscard?.branch}?</AlertDialogTitle>
            <AlertDialogDescription>
              Its commits are lost.
              {confirmDiscard && ["working", "verifying"].includes(confirmDiscard.status) ? " The running task will be stopped first." : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="discard-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="discard-confirm-action"
              onClick={() => { const card = confirmDiscard; setConfirmDiscard(null); if (card) performDiscard(card); }}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmRestore} onOpenChange={(open) => { if (!open) setConfirmRestore(null); }}>
        <AlertDialogContent data-testid="restore-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore to this checkpoint?</AlertDialogTitle>
            <AlertDialogDescription>
              Uncommitted changes on the branch will block the restore; later commits stay in git history but the branch resets.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="restore-confirm-action"
              onClick={() => {
                const r = confirmRestore; setConfirmRestore(null);
                if (r) send({ type: "checkpoint.restore", host_id: onlineHost?.host_id, project_path: project?.root_path,
                              branch: r.branch, is_main: r.is_main, commit: r.commit });
              }}>
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
